import { motion, useReducedMotion } from 'motion/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { PaintbrushVerticalIcon } from '@air/design-icons';

import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { WorkspaceAgentPanelZeroStateSuggestionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/WorkspaceAgentPanelZeroStateSuggestionRow';
import { isWorkspaceAgentGuidedVariantSuggestionId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import {
  getWorkspaceAgentHeroStructuredDisplay,
  getWorkspaceAgentPanelZeroStateSuggestionById,
  WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTIONS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { usePrivateWorkspaceAgentGuidedVariantFlowTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantFlowTracking';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import {
  usePrivateWorkspaceAgentPanelComposerPills,
  usePrivateWorkspaceAgentPanelSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { pinWorkspaceAgentScrollToBottom } from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';
import { getWorkspaceAgentGuidedVariantsOpenedEntryForSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantAnalytics';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

const SUGGESTION_ENTRANCE_S = 0.22;

/** Matches `WorkspaceAgentPanelZeroStateSuggestionRow` so the invite reads like the zero state. */
const TRY_IT_ENTRANCE_TRANSLATE_Y_PX = 6;

const ONBOARDING_TRY_IT_SUGGESTIONS = WORKSPACE_AGENT_PANEL_ZERO_STATE_SUGGESTIONS.map((suggestion) =>
  isWorkspaceAgentGuidedVariantSuggestionId(suggestion.id)
    ? { ...suggestion, icon: PaintbrushVerticalIcon, title: 'Make edits and variants' }
    : suggestion,
);

/**
 * The handoff under a finished onboarding run. The demo stays above the rule; these are the real
 * suggestions the panel offers once it is the user's turn.
 */
export const PrivateWorkspaceAgentOnboardingTryIt = memo(() => {
  const { hasExplicitSelection } = usePrivateWorkspaceAgentPanelComposerPills();
  const { sessionId } = usePrivateWorkspaceAgentPanelSession();
  const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const { seedComposer } = useWorkspaceAgentComposerSeed();
  const { trackGuidedVariantFlowOpened } = usePrivateWorkspaceAgentGuidedVariantFlowTracking({ chatId: sessionId });
  const isGuidedVariantFlowOpen = useWorkspaceAgentGuidedVariantFlowStore((state) => state.isGuidedVariantFlowOpen);
  const openGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.openGuidedVariantFlow);
  const shouldReduceMotion = !!useReducedMotion();
  const transitionDuration = shouldReduceMotion ? 0 : SUGGESTION_ENTRANCE_S;
  const rootRef = useRef<HTMLDivElement>(null);
  // Flip after mount so Motion runs initial → animate (same pattern as the zero-state chips).
  const [isEntered, setIsEntered] = useState(shouldReduceMotion);

  // The demo is not a session message, so chat auto-scroll never runs. Pin once when this mounts.
  useIsomorphicLayoutEffect(() => {
    const container = rootRef.current?.closest(`[${WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE}]`);

    if (!(container instanceof HTMLElement)) {
      return;
    }

    pinWorkspaceAgentScrollToBottom(container, 'instant');
  }, []);

  useEffect(() => {
    if (shouldReduceMotion) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsEntered(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [shouldReduceMotion]);

  const handleSuggestionSelect = useCallback(
    (suggestionId: string) => {
      if (blockIfCreditsDepleted()) {
        return;
      }

      if (isWorkspaceAgentGuidedVariantSuggestionId(suggestionId)) {
        trackGuidedVariantFlowOpened({
          entry: getWorkspaceAgentGuidedVariantsOpenedEntryForSuggestion(suggestionId),
        });
        openGuidedVariantFlow();
        return;
      }

      const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

      if (!suggestion) {
        return;
      }

      seedComposer(suggestion.prompt, {
        sendImmediately: hasExplicitSelection,
        source: 'hero_chip',
        structuredDisplay: hasExplicitSelection ? getWorkspaceAgentHeroStructuredDisplay(suggestionId) : undefined,
      });
    },
    [blockIfCreditsDepleted, hasExplicitSelection, openGuidedVariantFlow, seedComposer, trackGuidedVariantFlowOpened],
  );

  if (isGuidedVariantFlowOpen) {
    return null;
  }

  return (
    <div className="flex flex-col pb-6" data-testid="WORKSPACE_AGENT_ONBOARDING_TRY_IT" ref={rootRef}>
      <motion.div
        animate={{ opacity: isEntered ? 1 : 0 }}
        aria-hidden
        className="pointer-events-none relative z-1 -mt-24 h-24 bg-gradient-to-t from-gray-1 via-gray-1/70 to-transparent"
        initial={{ opacity: 0 }}
        transition={{ duration: transitionDuration, ease: 'easeOut' }}
      />
      <div className="flex flex-col gap-3 border-gray-4 border-t px-1 pt-5">
        <motion.div
          animate={{
            opacity: isEntered ? 1 : 0,
            y: isEntered ? 0 : TRY_IT_ENTRANCE_TRANSLATE_Y_PX,
          }}
          initial={{ opacity: 0, y: TRY_IT_ENTRANCE_TRANSLATE_Y_PX }}
          transition={{ duration: transitionDuration, ease: 'easeOut' }}
        >
          <p className="mb-1 font-semibold text-14 text-gray-12 leading-5">Now it's your turn.</p>
          <p className="text-14 text-gray-12 leading-5">Try one of these, or describe what you need.</p>
        </motion.div>
        <div className="flex flex-col items-start gap-2 overflow-visible py-0.5">
          {ONBOARDING_TRY_IT_SUGGESTIONS.map((suggestion, index) => (
            <WorkspaceAgentPanelZeroStateSuggestionRow
              index={index + 1}
              isDisabled={isCreditsDepleted}
              isVisible={isEntered}
              key={suggestion.id}
              onSelect={handleSuggestionSelect}
              showQuickReplyShimmer
              slotCount={ONBOARDING_TRY_IT_SUGGESTIONS.length}
              suggestion={suggestion}
              transitionDuration={transitionDuration}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

PrivateWorkspaceAgentOnboardingTryIt.displayName = 'PrivateWorkspaceAgentOnboardingTryIt';
